import {
    Component,
    ChangeDetectionStrategy,
    OnInit,
    Type,
    ChangeDetectorRef,
    OnDestroy,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
    FieldTypeConfig,
    FormlyFieldConfig,
    FormlyModule,
} from '@ngx-formly/core';
import { FieldType, FormlyFieldProps } from '@ngx-formly/material/form-field';
import { MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { Subject, distinctUntilChanged, map, take, Observable, startWith, of, takeUntil } from 'rxjs';
import { Message } from '@intellio/shared/models';
import { MatIconModule } from "@angular/material/icon";
import { compareIntelligentActionResults, computeDynamicResultMessage, parseSerialNumberList } from '@intellio/shared/utils';

// LocaL type used to bundle message contents and styling based on check results
type MessageObject = {
    message: string,
    match: boolean,
    icon: string,
    colorClass: string,
}


interface InputProps extends FormlyFieldProps {
    result$: any;
    intelligentActionName: string;
    notLoaded: boolean;
    valuesMatch: boolean;
    comparison?: string;
    wrapComparison?: boolean;
    onReviewValueChange?: (value: string) => void;
}

export interface FormlyInputFieldConfig extends FormlyFieldConfig<InputProps> {
    type: 'input' | Type<IntelligentActionComponent>;
}

@Component({
    selector: 'itc-formly-intelligent-action',
    styleUrls: ['./intelligent-action.component.scss'],
    template: `
        <span class='checkTitle'>{{ props.intelligentActionName }}</span>
        <div class='checkContainer' [class.compositeComparison]="!!props.wrapComparison || isSerialNumberComparison">
          <mat-form-field>
            @if (isSerialNumberComparison) {
              <mat-label i18n>Nameplate Serial Numbers</mat-label>
              <textarea matInput [id]="id" [formControl]="extractedControl" rows="4"
                [readonly]="props.readonly || props.disabled"
                [errorStateMatcher]="errorStateMatcher" [formlyAttributes]="field"></textarea>
            } @else {
            <mat-label i18n>Extracted Data</mat-label>
            <input
              matInput
              [id]="id"
              [formControl]="extractedControl"
              [readonly]="props.readonly || props.disabled"
              [errorStateMatcher]="errorStateMatcher"
              [formlyAttributes]="field"
              />
            }
            </mat-form-field>
            <mat-form-field>
              @if (isSerialNumberComparison) {
                <mat-label i18n>Serial-Number List</mat-label>
                <textarea matInput [id]="id + '-actual'" [formControl]="actualControl"
                  rows="4" [readonly]="true"></textarea>
              } @else {
              <mat-label i18n>Form Data</mat-label>
              <input
                matInput
                [id]="id + '-actual'"
                [formControl]="actualControl"
                [readonly]="true"
                />
              }
              </mat-form-field>
              @if (message$ | async; as message) {
                <div
                  class="checkMessageDiv"
                  [ngClass]="message.match ? 'successMessageStyle' : 'failMessageStyle'">
                  <mat-icon class="checkMessageIcon">{{message.icon}}</mat-icon>
                  <span class="checkMessageText">
                    {{message.message}}
                  </span>
                </div>
              }
            </div>
        `,
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatLabel,
        FormlyModule,
        MatInputModule,
        ReactiveFormsModule,
        CommonModule,
        MatIconModule
    ],
})
export class IntelligentActionComponent
    extends FieldType<FieldTypeConfig<InputProps>>
    implements OnInit, OnDestroy {
    constructor(protected cd: ChangeDetectorRef) {
        super();
    }

    control: UntypedFormControl;
    override defaultOptions = {
        props: {
            appearance: 'fill' as MatFormFieldAppearance,
            result$: null,
            intelligentActionName: '',
            notLoaded: true,
            valuesMatch: false
        },
    };
    extractedControl: UntypedFormControl;
    actualControl: UntypedFormControl;
    onDestroy$ = new Subject<void>();
    extractedValue: string;
    applicationValue: string;
    isEmptyOnLoad: boolean = true;
    message$: Observable<MessageObject> = of({
        message: '',
        match: false,
        icon: '',
        colorClass: ''
    });
    notLoadedMessage: string = "No data found, please wait then refresh"

    get isSerialNumberComparison(): boolean {
        return this.props.comparison === 'serialNumberOverlap';
    }

    private comparisonValue(value: any): any {
        // Textarea content is literal serial text, even when an identifier starts with [ or {.
        return this.isSerialNumberComparison
            ? String(value ?? '').split(/\r?\n/).map(serial => serial.trim()).filter(Boolean)
            : value;
    }

    private savedValue(value: any): any {
        return this.isSerialNumberComparison ? JSON.stringify(this.comparisonValue(value)) : value;
    }

    ngOnInit(): void {
        this.extractedControl = new UntypedFormControl('');
        this.actualControl = new UntypedFormControl('');

        this.setFormValues();
        this.setDynamicResultMessage();

        this.extractedControl.valueChanges.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(
            {
                next: (newValue) => {

                    if (compareIntelligentActionResults(this.comparisonValue(this.extractedControl.value), this.comparisonValue(this.actualControl.value), this.props.comparison)) {
                        this.props.valuesMatch = true;
                    } else {
                        this.props.valuesMatch = false;
                    }

                    this.formControl.setValue({
                        ...this.formControl.value,
                        actualControl: this.savedValue(this.actualControl.value),
                        extractedControl: this.savedValue(newValue)
                    });
                    this.props.onReviewValueChange?.(newValue ?? '');
                },
                error: (err) => console.log(err),
                complete: () => { }
            });


    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    setDynamicResultMessage() {
        this.message$ = this.extractedControl.valueChanges.pipe(
            startWith(this.extractedControl.value),
            map((extractedValue): MessageObject => {
                const actualValue = this.comparisonValue(this.actualControl.value);
                const comparisonExtracted = this.comparisonValue(extractedValue);
                var isMatch = compareIntelligentActionResults(comparisonExtracted, actualValue, this.props.comparison);
                if (!actualValue && !extractedValue) {
                    isMatch = false;
                }
                const messageContent = computeDynamicResultMessage(comparisonExtracted, actualValue, this.props.notLoaded, this.props.comparison, this.isEmptyOnLoad);
                return {
                    message: messageContent,
                    match: isMatch,
                    icon: isMatch ? 'check_circle' : 'error',
                    colorClass: isMatch ? 'successMessage' : 'failMessage',
                };
            })
        );
    }

    setFormValues() {
        if (!this.props.result$) return;

        this.props.result$
            .pipe(
                takeUntil(this.onDestroy$),
                map((r: any) => {
                    //if nothing was emitted, pass this along
                    if (r === null) {
                        return r;
                    }
                    return {
                        current: r?.content?.currentValue,
                        actual: r?.content?.actualValue === null ? '' : r.content.actualValue
                    }
                })
            )
            .subscribe((val) => {
                if (!val) {
                    this.message$ = of({
                        message: this.notLoadedMessage,
                        match: false,
                        icon: 'error',
                        colorClass: 'failMessage',
                    });
                    return;
                }
                var current = this.isSerialNumberComparison ? parseSerialNumberList(val.current).join('\n') : val.current;
                var actual = this.isSerialNumberComparison ? parseSerialNumberList(val.actual).join('\n') : val.actual;
                if (current) {
                    this.isEmptyOnLoad = false;
                }

                if (compareIntelligentActionResults(this.comparisonValue(current), this.comparisonValue(actual), this.props.comparison)) {
                    this.props.valuesMatch = true;
                } else {
                    this.props.valuesMatch = false;
                }

                if (current || actual) {
                    this.actualControl.setValue(actual);
                    this.extractedControl.setValue(current);
                }

                this.formControl.setValue({
                    ...this.formControl.value,
                    actualControl: this.savedValue(actual),
                    extractedControl: this.savedValue(current)
                });

            });
    }
}
